import { api } from './api';
import type { Order } from './orders';

export interface DailyStats {
  readonly date: string;
  readonly ordersCount: number;
  readonly quotesCount: number;
  readonly revenue: number;
}

export interface StaleOrder {
  readonly orderId: string;
  readonly status: string;
  readonly staleDays: number;
  readonly customerEmail: string | null;
  readonly createdAt: string;
}

export interface DashboardStats {
  readonly totalRevenue: number;
  readonly totalOrders: number;
  readonly totalQuotes: number;
  readonly revenueByDay: readonly DailyStats[];
  readonly ordersByStatus: Record<string, number>;
  readonly activeQueueCount: number;
  readonly waitingOnAdminCount: number;
  readonly averageTurnaroundDays: number | null;
  readonly printFailureCount: number;
  readonly reprintCount: number;
  readonly failureRate: number;
  readonly topMaterials: Readonly<Record<string, number>>;
  readonly staleOrders: readonly StaleOrder[];
  readonly totalCancellations: number;
  readonly totalRefundAmount: number;
}

export interface ShippingRate {
  readonly label: string;
  readonly amount: number;
  readonly minDeliveryDays: number | null;
  readonly maxDeliveryDays: number | null;
}

export interface WorkflowConfig {
  readonly autoAdvancePaidToQueued: boolean;
  readonly staleDaysThreshold: Readonly<Record<string, number>>;
  readonly shippingRates: readonly ShippingRate[];
  readonly freeShippingThreshold: number | null;
}

export interface AuditLogItem {
  readonly auditId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly action: string;
  readonly actor: string;
  readonly changes: Record<string, unknown> | null;
  readonly note: string | null;
  readonly metadata: Record<string, unknown> | null;
  readonly timestamp: string;
}

export interface FailureAnalytics {
  readonly totalFailures: number;
  readonly totalReprints: number;
  readonly failureRate: number;
  readonly avgReprintsPerFailure: number;
  readonly totalPrintTimeMinutesLost: number;
  readonly failuresByCategory: Readonly<Record<string, number>>;
  readonly failuresByMaterial: Readonly<Record<string, number>>;
  readonly failuresByColor: Readonly<Record<string, number>>;
  readonly rejectionReasons: Readonly<Record<string, number>>;
}

export interface WebhookError {
  readonly errorId: string;
  readonly eventType: string;
  readonly stripeEventId: string | null;
  readonly errorMessage: string;
  readonly rawPayload: string;
  readonly resolved: boolean;
  readonly timestamp: string;
}

export interface AdminQuote {
  readonly quoteId: string;
  readonly userId: string;
  readonly fileName: string;
  readonly volumeCm3: number;
  readonly status: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface AdminQuoteDetail {
  readonly quoteId: string;
  readonly userId: string;
  readonly fileKey: string;
  readonly fileName: string;
  readonly fileType: string | null;
  readonly fileSizeBytes: number | null;
  readonly volumeCm3: number;
  readonly surfaceAreaCm2: number;
  readonly boundingBox: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  } | null;
  readonly prices: readonly {
    readonly materialId: string;
    readonly price: number;
    readonly rawPrice: number;
    readonly estimatedDays: number;
  }[];
  readonly status: string;
  readonly autoEstimate: number | null;
  readonly manualOverridePrice: number | null;
  readonly reviewStatus: 'pending' | 'approved' | 'rejected' | null;
  readonly rejectionReason: string | null;
  readonly customerInstructions: string | null;
  readonly adminReviewNotes: string | null;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly downloadUrl: string | null;
}

export interface ReviewQuoteInput {
  readonly action: 'approve' | 'reject';
  readonly adminReviewNotes?: string;
  readonly rejectionReason?: string;
  readonly manualPrices?: readonly {
    readonly materialId: string;
    readonly price: number;
    readonly rawPrice: number;
    readonly estimatedDays: number;
  }[];
}

export interface ColorSpoolInfo {
  readonly spoolCost: number;
  readonly spoolWeightGrams: number;
}

export interface MaterialWithStock {
  readonly materialId: string;
  readonly name: string;
  readonly description: string;
  readonly ratePerCm3: number;
  readonly baseFee: number;
  readonly finishRate: number;
  readonly densityGPerCm3: number;
  readonly estimatedDaysBase: number;
  readonly colorOptions: readonly string[];
  readonly isActive: boolean;
  readonly colorStock: Readonly<Record<string, boolean>>;
  readonly colorSpoolInfo: Readonly<Record<string, ColorSpoolInfo>>;
}

export interface CreateMaterialInput {
  readonly name: string;
  readonly description: string;
  readonly defaultSpoolCost: number;
  readonly defaultSpoolWeightGrams: number;
  readonly densityGPerCm3: number;
  readonly colorOptions: readonly string[];
  readonly estimatedDaysBase: number;
}

export interface UpdateMaterialInput {
  readonly name?: string;
  readonly description?: string;
  readonly densityGPerCm3?: number;
  readonly estimatedDaysBase?: number;
  readonly isActive?: boolean;
  readonly colorOptions?: readonly string[];
  readonly colorStock?: Record<string, boolean>;
  readonly colorSpoolInfo?: Record<string, ColorSpoolInfo>;
}

export interface PaginatedResponse<T> {
  readonly data: readonly T[];
  readonly pagination: { readonly lastKey: string | null };
}

export interface Coupon {
  readonly couponId: string;
  readonly code: string;
  readonly description: string;
  readonly percentOff: number | null;
  readonly freeShipping: boolean;
  readonly waiveMinimum: boolean;
  readonly maxUses: number | null;
  readonly usedCount: number;
  readonly expiresAt: string | null;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateCouponInput {
  readonly code: string;
  readonly description: string;
  readonly percentOff: number | null;
  readonly freeShipping: boolean;
  readonly waiveMinimum: boolean;
  readonly maxUses: number | null;
  readonly expiresAt: string | null;
}

export interface UpdateCouponInput {
  readonly description?: string;
  readonly percentOff?: number | null;
  readonly freeShipping?: boolean;
  readonly waiveMinimum?: boolean;
  readonly maxUses?: number | null;
  readonly expiresAt?: string | null;
  readonly isActive?: boolean;
}

export const adminApi = {
  getDashboardStats: async (days = 30): Promise<DashboardStats> => {
    const result = await api.get<DashboardStats>(`/admin/stats?days=${days}`);
    if (!result.data) throw new Error('Failed to fetch dashboard stats');
    return result.data;
  },

  listOrders: async (params?: {
    status?: string;
    lastKey?: string;
    limit?: number;
  }): Promise<PaginatedResponse<Order>> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.lastKey) query.set('lastKey', params.lastKey);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const result = await api.get<readonly Order[]>(`/admin/orders${qs ? `?${qs}` : ''}`);
    return {
      data: result.data ?? [],
      pagination: result.pagination ?? { lastKey: null },
    };
  },

  listQuotes: async (params?: {
    status?: string;
    limit?: number;
  }): Promise<PaginatedResponse<AdminQuote>> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const result = await api.get<readonly AdminQuote[]>(`/admin/quotes${qs ? `?${qs}` : ''}`);
    return {
      data: result.data ?? [],
      pagination: result.pagination ?? { lastKey: null },
    };
  },

  updateOrderStatus: async (
    orderId: string,
    status: string,
  ): Promise<void> => {
    await api.put(`/orders/${orderId}/status`, { status });
  },

  updateTracking: async (
    orderId: string,
    trackingNumber: string,
    trackingCarrier?: string,
  ): Promise<void> => {
    await api.put(`/admin/orders/${orderId}/tracking`, {
      trackingNumber,
      ...(trackingCarrier ? { trackingCarrier } : {}),
    });
  },

  cancelOrder: async (
    orderId: string,
    action: 'cancel' | 'reject',
    reason: string,
  ): Promise<void> => {
    await api.post(`/admin/orders/${orderId}/cancel`, { action, reason });
  },

  getMaterials: async (): Promise<readonly MaterialWithStock[]> => {
    const result = await api.get<readonly MaterialWithStock[]>('/admin/materials');
    return result.data ?? [];
  },

  updateMaterialStock: async (
    materialId: string,
    updates: { colorStock?: Record<string, boolean>; isActive?: boolean },
  ): Promise<void> => {
    await api.put(`/admin/materials/${materialId}/stock`, updates);
  },

  createMaterial: async (input: CreateMaterialInput): Promise<MaterialWithStock> => {
    const result = await api.post<MaterialWithStock>('/admin/materials', input);
    return result.data!;
  },

  updateMaterial: async (
    materialId: string,
    updates: UpdateMaterialInput,
  ): Promise<MaterialWithStock> => {
    const result = await api.put<MaterialWithStock>(`/admin/materials/${materialId}`, updates);
    return result.data!;
  },

  deleteMaterial: async (materialId: string): Promise<void> => {
    await api.delete(`/admin/materials/${materialId}`);
  },

  updateProduction: async (
    orderId: string,
    data: Record<string, unknown>,
  ): Promise<void> => {
    await api.put(`/admin/orders/${orderId}/production`, data);
  },

  updateShipping: async (
    orderId: string,
    data: Record<string, unknown>,
  ): Promise<void> => {
    await api.put(`/admin/orders/${orderId}/shipping`, data);
  },

  updatePricing: async (
    orderId: string,
    data: Record<string, unknown>,
  ): Promise<void> => {
    await api.put(`/admin/orders/${orderId}/pricing`, data);
  },

  getWorkflowConfig: async (): Promise<WorkflowConfig> => {
    const result = await api.get<WorkflowConfig>('/admin/config/workflow');
    return result.data ?? {
      autoAdvancePaidToQueued: false,
      staleDaysThreshold: {},
      shippingRates: [{ label: 'Standard Shipping', amount: 5.99, minDeliveryDays: 5, maxDeliveryDays: 8 }],
      freeShippingThreshold: null,
    };
  },

  updateWorkflowConfig: async (config: Partial<WorkflowConfig>): Promise<WorkflowConfig> => {
    const result = await api.put<WorkflowConfig>('/admin/config/workflow', config);
    return result.data!;
  },

  getAuditTrail: async (entityId: string): Promise<readonly AuditLogItem[]> => {
    const result = await api.get<readonly AuditLogItem[]>(`/admin/audit/${entityId}`);
    return result.data ?? [];
  },

  getQueue: async (): Promise<readonly Order[]> => {
    const result = await api.get<readonly Order[]>('/admin/queue');
    return result.data ?? [];
  },

  updateQueue: async (items: readonly Record<string, unknown>[]): Promise<void> => {
    await api.put('/admin/queue', { items });
  },

  getFailureAnalytics: async (): Promise<FailureAnalytics> => {
    const result = await api.get<FailureAnalytics>('/admin/analytics/failures');
    return result.data!;
  },

  exportOrdersCsv: async (startDate: string, endDate: string): Promise<string> => {
    const response = await api.getRaw(`/admin/exports/orders?startDate=${startDate}&endDate=${endDate}`);
    return response;
  },

  exportRefundsCsv: async (startDate: string, endDate: string): Promise<string> => {
    const response = await api.getRaw(`/admin/exports/refunds?startDate=${startDate}&endDate=${endDate}`);
    return response;
  },

  getWebhookErrors: async (date?: string): Promise<readonly WebhookError[]> => {
    const qs = date ? `?date=${date}` : '';
    const result = await api.get<readonly WebhookError[]>(`/admin/webhook-errors${qs}`);
    return result.data ?? [];
  },

  exportOrderJson: async (orderId: string): Promise<Record<string, unknown>> => {
    const result = await api.get<Record<string, unknown>>(`/admin/orders/${orderId}/export`);
    return result.data!;
  },

  listCoupons: async (): Promise<readonly Coupon[]> => {
    const result = await api.get<readonly Coupon[]>('/admin/coupons');
    return result.data ?? [];
  },

  createCoupon: async (input: CreateCouponInput): Promise<Coupon> => {
    const result = await api.post<Coupon>('/admin/coupons', input);
    return result.data!;
  },

  updateCoupon: async (code: string, updates: UpdateCouponInput): Promise<Coupon> => {
    const result = await api.put<Coupon>(`/admin/coupons/${code}`, updates);
    return result.data!;
  },

  deleteCoupon: async (code: string): Promise<void> => {
    await api.delete(`/admin/coupons/${code}`);
  },

  getModelUploadUrl: async (fileName: string, fileSize: number, contentType: string) => {
    const result = await api.post<{ uploadUrl: string; fileKey: string }>(
      '/admin/model-upload-url',
      { fileName, fileSize, contentType },
    );
    return result.data!;
  },

  getQuote: async (quoteId: string): Promise<AdminQuoteDetail> => {
    const result = await api.get<AdminQuoteDetail>(`/admin/quotes/${quoteId}`);
    if (!result.data) throw new Error('Quote not found');
    return result.data;
  },

  reviewQuote: async (quoteId: string, input: ReviewQuoteInput): Promise<AdminQuoteDetail> => {
    const result = await api.put<AdminQuoteDetail>(`/admin/quotes/${quoteId}/review`, input);
    return result.data!;
  },

  analyzeModel: async (fileKey: string) => {
    const result = await api.post<{
      volumeCm3: number;
      surfaceAreaCm2: number;
      boundingBox: { width: number; height: number; depth: number };
      prices: readonly { materialId: string; price: number; rawPrice: number; estimatedDays: number }[];
    }>('/admin/analyze-model', { fileKey });
    return result.data!;
  },
};
